import React from 'react';
import { useLocalizedText } from '../../../hooks/localizedText';

import Container from '@amzn/awsui-components-react/polaris/container';
import Header from '@amzn/awsui-components-react/polaris/header';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Input from '@amzn/awsui-components-react/polaris/input';
import Textarea from '@amzn/awsui-components-react/polaris/textarea';
import Button from '@amzn/awsui-components-react/polaris/button';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Grid from '@amzn/awsui-components-react/polaris/grid';
import Popover from '@amzn/awsui-components-react/polaris/popover';

import { AmbassadorSelect } from './ambassadors';

import {
  EditableInfoField,
  optionsToValueArrayString,
} from '../../../hooks/events/editableEventInfo';

import { externalLinks } from '../../../data/externalLinks';

type PostEventInfoProps = {
  editableInfo: { [s in EditableInfoField]: string };
  updateEditableInfoFields: (fields: { [s in EditableInfoField]?: string }) => void;
  eventErrors?: { [s in EditableInfoField]?: string };
};

function OptionalLabel({ children }: { children: string }) {
  const namespaces = ['attendance', 'events', 'translation'];
  const { t } = useLocalizedText(namespaces);

  return (
    <span>
      {children} - <i>{t('additionalInfo.optional')}</i>
    </span>
  );
}

/**
 * Post-event Information section for listed events in the attendance flow.
 * This is a subset of AdditionalInfo, containing only fields that are NOT
 * available on the Edit Event page:
 *   - Additional Coordinators (ambassadors)
 *   - Organization Point of Contact
 *   - Engagement Recap
 *   - Photo Upload Link
 */
export function PostEventInfo({
  editableInfo,
  updateEditableInfoFields,
  eventErrors,
}: PostEventInfoProps) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useLocalizedText(namespaces);

  return (
    <Container header={<Header variant="h2">{t('postEventInfo.header')}</Header>}>
      <SpaceBetween direction="vertical" size="l">
        <FormField
          stretch
          label={<OptionalLabel>{t('additionalInfo.additionalCoordinators')}</OptionalLabel>}
          description={t('postEventInfo.coordinatorsDescription')}
          errorText={eventErrors?.registered_community_ambassadors}
        >
          <AmbassadorSelect
            currentAmbassadors={editableInfo.registered_community_ambassadors}
            placeholder={t('postEventInfo.coordinatorsPlaceholder')}
            onChange={({ detail }) => {
              updateEditableInfoFields({
                registered_community_ambassadors: optionsToValueArrayString(detail.selectedOptions),
              });
            }}
          />
        </FormField>

        <FormField
          stretch
          label={<OptionalLabel>{t('additionalInfo.organizationPointOfContact')}</OptionalLabel>}
        >
          <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
            <FormField
              stretch
              label={t('additionalInfo.organizationContactName')}
              errorText={eventErrors?.organization_point_of_contact_name}
            >
              <Input
                value={editableInfo.organization_point_of_contact_name}
                onChange={({ detail }) =>
                  updateEditableInfoFields({ organization_point_of_contact_name: detail.value })
                }
              />
            </FormField>
            <FormField
              stretch
              label={t('additionalInfo.organizationContactEmail')}
              errorText={eventErrors?.organization_point_of_contact_email}
            >
              <Input
                value={editableInfo.organization_point_of_contact_email}
                onChange={({ detail }) =>
                  updateEditableInfoFields({ organization_point_of_contact_email: detail.value })
                }
                type="email"
              />
            </FormField>
          </Grid>
        </FormField>

        <FormField
          stretch
          label={<OptionalLabel>{t('additionalInfo.engagementRecap')}</OptionalLabel>}
          description={t('additionalInfo.engagementRecapDescription')}
          errorText={eventErrors?.engagement_recap}
        >
          <Textarea
            value={editableInfo.engagement_recap}
            onChange={({ detail }) => updateEditableInfoFields({ engagement_recap: detail.value })}
            rows={4}
          />
        </FormField>

        <FormField
          stretch
          label={
            <SpaceBetween direction="horizontal" size="l">
              <OptionalLabel>{t('additionalInfo.photoUpload')}</OptionalLabel>
              <Popover
                dismissButton={false}
                position="top"
                size="small"
                triggerType="custom"
                header={t('additionalInfo.photoInfo.header')}
                content={t('additionalInfo.photoInfo.content')}
              >
                <Button variant="inline-link">
                  {t('grant.infoButton', { ns: 'translation' })}
                </Button>
              </Popover>
            </SpaceBetween>
          }
          description={t('additionalInfo.photoUploadDescription')}
          errorText={eventErrors?.photo_upload}
        >
          <SpaceBetween direction="horizontal" size="l">
            <Input
              value={editableInfo.photo_upload}
              onChange={({ detail }) => updateEditableInfoFields({ photo_upload: detail.value })}
            />

            <Button
              href={externalLinks.attendance.workdocs}
              data-aci-analytics-name="trackAttendance_workDocsLink"
              iconAlign="right"
              iconName="external"
              target="_blank"
            >
              {t('additionalInfo.chooseFiles')}
            </Button>
          </SpaceBetween>
        </FormField>
      </SpaceBetween>
    </Container>
  );
}
